
import React from 'react';
import { AnalysisResult, COLORS } from '../types';
import { createFocusAreaChartData } from '../utils';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from 'recharts';
import CustomTooltip from '../CustomTooltip';

type FocusAreasTabProps = {
  analysisResult: AnalysisResult;
};

const CustomYAxisTick = (props: any) => {
  const { x, y, payload } = props;
  return (
    <g transform={`translate(${x},${y})`}>
      <text 
        x={0} 
        y={0} 
        dy={4} 
        textAnchor="end" 
        fill="#fff" 
        fontSize={10}
        transform="rotate(-45)"
      >
        {payload.value}
      </text>
    </g>
  );
};

const FocusAreasTab = ({ analysisResult }: FocusAreasTabProps) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-secondary/30 rounded-lg p-6 border border-primary/10 space-y-4">
        {analysisResult.focusAreas?.map((area, index) => (
          <div key={index} className="bg-secondary/50 rounded-lg p-4 border border-primary/10">
            <h4 className="text-lg font-semibold text-primary mb-2">{area.title}</h4>
            <p className="text-white/80 mb-3 text-sm">{area.description}</p>
            <div className="bg-secondary/50 rounded p-3 mb-3">
              <h5 className="text-xs font-medium text-white/70 mb-1">Rationale</h5>
              <p className="text-sm text-white">{area.rationale}</p>
            </div>
            <div>
              <h5 className="text-xs font-medium text-white/70 mb-1">KPI Alignment</h5>
              <div className="flex flex-wrap gap-2">
                {area.kpiAlignment.map((kpi, kidx) => (
                  <span 
                    key={kidx} 
                    className="px-2 py-1 bg-indigo-800/30 text-indigo-300 rounded-full text-xs"
                  >
                    {kpi}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="bg-secondary/30 rounded-lg p-6 border border-primary/10">
        <h4 className="text-lg font-semibold text-primary mb-4">Focus Areas Impact</h4>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={createFocusAreaChartData(analysisResult)}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#444" />
              <XAxis 
                type="number" 
                domain={[0, 100]} 
                stroke="#888"
                tick={{ fill: '#fff', fontSize: 10 }}
              />
              <YAxis 
                dataKey="name" 
                type="category" 
                stroke="#888" 
                width={35}
                tick={<CustomYAxisTick />}
              />
              <Tooltip
                content={<CustomTooltip />}
                formatter={(value, name, props) => [
                  `Priority: ${value}%`,
                  'Impact',
                ]}
              />
              <Bar dataKey="value" fill="#8884d8">
                {createFocusAreaChartData(analysisResult).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-3 text-center">
          <p className="text-xs text-gray-400">
            Priority Scale: 0 = Low Priority, 25 = Medium-Low, 50 = Medium, 75 = High, 100 = Critical Priority
          </p>
        </div>
      </div>
    </div>
  );
};

export default FocusAreasTab;
